import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api       from "../api";
import { track } from "../utils/analytics";        

import "../styles/EmailVerificationPage.css";

const EmailVerificationPage = () => {
  /* ───────── state ───────── */
  const [verificationCode, setVerificationCode]   = useState("");
  const [verificationError, setVerificationError] = useState("");

  const [isResendDisabled, setIsResendDisabled]   = useState(false);
  const [resendCountdown,  setResendCountdown]    = useState(60);
  const [showCodeSentMessage, setShowCodeSentMessage] = useState(false);

  /* ───────── hooks ───────── */
  const navigate = useNavigate();
  const { user_id } = useParams();
  const accountType = localStorage.getItem("accountType") || "unknown";

  /* ───────── analytics: page view ───────── */
  useEffect(() => {
    track("page_view", {
      page_location: window.location.href,
      page_title   : "Email Verification",
    });
  }, []);

  /* ───────── resend timer ───────── */
  useEffect(() => {
    let timer;
    if (isResendDisabled) {
      timer = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsResendDisabled(false);
            setShowCodeSentMessage(false);
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isResendDisabled]);

  /* ───────── handlers ───────── */

  const handleVerify = async (event) => {
    event.preventDefault();
    track("verify_code_submit", { account_type: accountType });

    try {
      const { data } = await api.get("/check_passcode/", {
        params: { user_id, passcode: verificationCode },
      });

      if (data.status === "success") {
        track("verify_code_success", { account_type: accountType });
        window.gtag?.("set", "user_properties", { email_verified: true });

        if (accountType === "business") navigate(`/business_plans/${user_id}`);
        else if (accountType === "couple") navigate(`/couples_plans/${user_id}`);
        else navigate(`/dashboard/${user_id}`);
      } else {
        setVerificationError("Verification failed. Please try again.");
        track("verify_code_error", {
          account_type: accountType,
          message: "invalid_code",
        });
      }
    } catch (error) {
      setVerificationError("An error occurred during verification");
      track("verify_code_error", {
        account_type: accountType,
        message: error.message || "api_error",
      });
    }
  };

  const handleResendCode = async () => {
    track("resend_code_click", { account_type: accountType });

    try {
      const { data } = await api.get("/email_verification/", {
        params: { user_id },
      });

      if (data.status === "success") {
        setIsResendDisabled(true);
        setResendCountdown(60);
        setShowCodeSentMessage(true);

        track("resend_code_success", { account_type: accountType });
      } else if (data.remaining) {
        setIsResendDisabled(true);
        setResendCountdown(data.remaining);
        setShowCodeSentMessage(true);

        track("resend_code_rate_limited", {
          account_type: accountType,
          remaining: data.remaining,
        });
      } else {
        setVerificationError(data.message || "Failed to resend code.");
        track("resend_code_error", {
          account_type: accountType,
          message: data.message || "api_error",
        });
      }
    } catch (error) {
      setVerificationError("An error occurred while resending the code");
      track("resend_code_error", {
        account_type: accountType,
        message: error.message || "api_error",
      });
    }
  };

  const goBack = () => {
    track("navigation_click", {
      location   : "email_verification",
      nav_item   : "Back",
      destination: "history_back",
    });
    navigate(-1);
  };

  /* ───────── render ───────── */
  return (
    <div className="email-verification-body">
      <div className="email-verification-container">
        <div
          className="logo"
          onClick={() =>
            track("navigation_click", {
              location   : "email_verification",
              nav_item   : "Logo",
              destination: "/",
            })
          }
        >
          <a href="/">BestDay2Marry</a>
        </div>

        <div className="email-verification-form">
          <h1>Email Authentication</h1>
          <p>
            Please check your email for an authentication code. If not present,
            please check your spam.
          </p>

          <form onSubmit={handleVerify}>
            <div className="form-group">
              <input
                type="text"
                placeholder="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className={verificationError ? "error" : ""}
              />
              {verificationError && (
                <div className="error-message">{verificationError}</div>
              )}
            </div>

            <button type="submit" className="submit">
              Verify
            </button>

            <button
              type="button"
              className="tertiary-button"
              onClick={goBack}
            >
              Back
            </button>
          </form>

          <div className="alternative-verification">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isResendDisabled}
              className="resend-code-button"
            >
              Resend Verification Code
            </button>

            {showCodeSentMessage && isResendDisabled && (
              <div className="code-sent-message">
                A verification code has been sent! You can request another code
                in {resendCountdown} second{resendCountdown !== 1 ? "s" : ""}.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;